import React, { useState } from 'react';
import { Col, Button, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProductAssociationLookup from '../types/ProductAssociationLookup';
import { FormSegment, FormSegmentData } from './FormSegment';
import { createProductSegment } from '../api/productAlerts.api';
import { addProductAssociationLookup } from '../api/productAssociation.api';
import { EditProductAssociationModal } from '../EditProductAssociationModal';
import styles from './FormSegment.module.less';
import { defaultErrorMessage } from 'src/constants/errorMessages';
import { getMessage } from 'src/utils/ErrorUtils';

type FormCreateSegmentProps = {
  closeModal: () => void;
  onComplete: () => void;
};

const duplicateWord = 'Duplicate';
const errorDuplicateMessage = 'A product segment already exists with that name within your organization. Please update the segment name.';
const messageOptions = { content: '', formMessageKey: 'formState', duration: 2 };

const renderFormFooter = (
  disabledSubmit: boolean,
  data: FormSegmentData,
  onConfigure: (data: FormSegmentData) => void
) => {
  return (
    <>
      <Col span={10}>
        <Button block type="primary" htmlType="submit" disabled={disabledSubmit} className={styles.button}>
          Submit
        </Button>
      </Col>
      <Col span={4} className={styles.or}>
        or
      </Col>
      <Col span={10}>
        <Button block disabled={disabledSubmit} className={styles.button} onClick={() => onConfigure(data)}>
          <FontAwesomeIcon icon={['fas', 'tools']} />
          Configure Products
        </Button>
      </Col>
    </>
  );
};

export const FormCreateSegment: React.FC<FormCreateSegmentProps> = ({ closeModal, onComplete }) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [productName, setProductName] = useState<string>('');
  const [productSegmentId, setProductSegmentId] = useState<string | null>(null);

  const handleSubmitForm = async (data: FormSegmentData) => {
    await createProductSegment(data);
    onComplete();
  };

  const handleProductAssociationSubmit = async (data: ProductAssociationLookup) => {
    try {
      if (productSegmentId) {
        await addProductAssociationLookup({ ...data, productSegmentId });
        message.success('Product Association Complete');
      }
      setIsOpenModal(false);
      closeModal();
      onComplete();
    } catch (e) {
      message.error({
        ...messageOptions,
        content: defaultErrorMessage,
      });
      console.error(e);
    }
    return productSegmentId;
  };

  const handleConfigure = async (data: FormSegmentData) => {
    const hideLoading = message.loading('Loading...', 0);
    try {
      const productSegment = await createProductSegment(data);
      const productSegmentId = String(productSegment.responseHeader.id);
      setProductSegmentId(productSegmentId);
      message.success({ ...messageOptions, content: productSegment.responseHeader.statusMessage });
      setIsOpenModal(true);
    } catch (e) {
      let errorMessage = defaultErrorMessage;
      if (getMessage(e)?.includes(duplicateWord)) {
        errorMessage = errorDuplicateMessage;
      }
      message.error({
        ...messageOptions,
        content: errorMessage,
      });
      console.error(e);
    } finally {
      hideLoading();
    }
  };

  return (
    <>
      <FormSegment
        productSegment={null}
        closeModal={closeModal}
        onSubmitForm={handleSubmitForm}
        formTitle="Create Product Segment"
        successMessage="Product Segment has been created!"
        renderFormFooter={(disabledSubmit, data) => renderFormFooter(disabledSubmit, data, handleConfigure)}
        onProductNameChange={setProductName}
      />
      {isOpenModal && (
        <EditProductAssociationModal
          isOpenModal={isOpenModal}
          hideModal={() => {
            onComplete();
            setIsOpenModal(false);
            closeModal();
          }}
          productName={productName}
          onSubmit={handleProductAssociationSubmit}
        />
      )}
    </>
  );
};
